<template>

    <div>

        <ColorsInput :label="bgLabel"
                     :instruction="bgInstruction"
                     :colors="colors"
                     :options="options"
                     :options-property="bgProperty"
                     :contrast-with="options[textProperty]"
                     :contrast-level="contrastLevel"
                     :open="open"
        />

        <ColorsInput :label="textLabel"
                     :instruction="textInstruction"
                     :colors="colors"
                     :options="options"
                     :options-property="textProperty"
                     :contrast-with="options[bgProperty]"
                     :contrast-level="contrastLevel"
                     :open="open"
        />

    </div>

</template>

<script>

/*
 * BackgroundAndTextColorInput
 * ----------------------------------------------------------------------
 * Pair of bg + text colour pickers that cross-reference each other for
 * WCAG AA contrast (when `window.galaxyWcag` is true). Pick green as the
 * background → pink swatch greys out in the text picker because the
 * pair fails 4.5:1.
 *
 * Where to use:
 *   - CommonInputs (block-level bg/text — the existing case).
 *   - Any LoopItemPanel where a loop entry carries its own bg + text
 *     (e.g. a pricing card where each tier picks its own theme): drop
 *     this component into the panel and bind to the loop item's option
 *     keys via `bg-property` / `text-property`.
 *
 * The contrast logic lives in `ColorsInput` — this is just a convenience
 * wrapper that keeps the pair in sync without forcing every panel to
 * remember the cross-reference + colour-loader boilerplate.
 *
 * Override `bgProperty` / `textProperty` to use different option keys
 * (defaults match CommonInputs: `color` + `textColor`).
 */

import ColorsInput from "./ColorsInput";
import getDatabaseColors from "@galaxy/modules/Canvas/resources/components/canvas/blocks/utils/text/admin/getDatabaseColors";

export default {

    components: {
        ColorsInput,
    },

    props: {
        options: {
            type: Object,
            required: true,
        },

        // Option keys that hold the bg + text colour values. Defaults
        // match the long-standing CommonInputs convention — override on
        // a loop item panel where the keys differ (e.g. `cardBg` /
        // `cardTextColor`).
        bgProperty: {
            type: String,
            default: 'color',
        },
        textProperty: {
            type: String,
            default: 'textColor',
        },

        // Labels + instructions shown above each picker. Defaults match
        // the existing CommonInputs strings so nothing visibly changes
        // when the parent panel switches over.
        bgLabel: {
            type: String,
            default: 'Achtergrondkleur',
        },
        bgInstruction: {
            type: String,
            default: 'Bepaal hier de achtergrondkleur van het gehele blok.',
        },
        textLabel: {
            type: String,
            default: 'Tekstkleur',
        },
        textInstruction: {
            type: String,
            default: 'Bepaal hier de tekstkleur van het gehele blok.',
        },

        // WCAG target. 'AA' = 4.5:1 body text (default). 'AA_LARGE' =
        // 3:1 for UI / ≥18pt. 'AAA' = stricter 7:1.
        contrastLevel: {
            type: String,
            default: 'AA',
        },

        open: {
            type: Boolean,
            default: null,
        },
    },

    data() {
        return {
            colors: null,
        };
    },

    created() {
        // Vue 2 reactivity gotcha: properties that don't exist on `options`
        // when it's first observed are NOT reactive. If we leave `color` /
        // `textColor` undefined and later add them via `$set`, the cross-
        // referenced `:contrast-with` bindings on the sibling picker won't
        // re-evaluate because no dependency was tracked on the access of
        // the (then-non-existent) key. Force them to exist up front so
        // every read in the templates registers a real reactive dep.
        if (!Object.prototype.hasOwnProperty.call(this.options, this.bgProperty)) {
            this.$set(this.options, this.bgProperty, null);
        }
        if (!Object.prototype.hasOwnProperty.call(this.options, this.textProperty)) {
            this.$set(this.options, this.textProperty, null);
        }
    },

    mounted() {
        this.loadColors();
    },

    methods: {

        async loadColors() {
            // Pull palette from the database. The endpoint returns a
            // multi-level structure: top-level keys → arrays → either
            // `{label: hex}` objects OR plain `[hex]` arrays. We flatten
            // it into a single {label: hex} map.
            //
            // Output order is enforced as:
            //   1. transparent
            //   2. white
            //   3. black
            //   4. everything else (custom theme colours, in DB order)
            // — picker always opens with the same neutral lead-in before
            // the brand swatches.
            const databaseColors = (await getDatabaseColors(true)).data;

            const isNeutralValue = (v) => {
                if (typeof v !== 'string') return false;
                const lower = v.toLowerCase().trim();
                return lower === 'white' || lower === 'black' || lower === 'transparent'
                    || lower === '#ffffff' || lower === '#fff'
                    || lower === '#000000' || lower === '#000';
            };

            // Flatten the nested structure into ordered [label, hex] pairs.
            const customPairs = [];
            const addPair = (label, hex) => {
                if (typeof hex !== 'string') return;
                if (isNeutralValue(hex)) return;          // dedupe neutrals
                let normLabel = String(label);
                // JS object iteration places integer-like string keys
                // (`"1"`, `"2"`, ...) FIRST, before non-integer keys —
                // which would push numeric-labelled DB colours ahead of
                // transparent/white/black. Replace those with the hex
                // value so the key is unambiguously non-integer.
                if (/^-?\d+$/.test(normLabel)) {
                    normLabel = hex;
                }
                customPairs.push([normLabel, hex]);
            };

            Object.values(databaseColors).forEach((entry) => {
                // Top-level value is typically an array.
                const items = Array.isArray(entry) ? entry : [entry];
                items.forEach((item) => {
                    if (typeof item === 'string') {
                        addPair(item, item);
                    } else if (Array.isArray(item)) {
                        // Plain hex array: `[ "#056248" ]`. Use the hex as label.
                        item.forEach((hex) => addPair(hex, hex));
                    } else if (item && typeof item === 'object') {
                        // `{label: hex}` map: e.g. `{ "primary-inverse": "#ffffff" }`.
                        for (const [k, v] of Object.entries(item)) addPair(k, v);
                    }
                });
            });

            const formatted = {
                transparent: 'transparent',
                white: 'white',
                black: 'black',
            };
            for (const [label, hex] of customPairs) {
                formatted[label] = hex;
            }
            this.colors = formatted;
        },

    },

};

</script>
